/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {EventCodeValueType, getEventName} from '@ion/core/src/services/EventCodes';
import {GenericObject} from '@ion/core/src/types/entities';
import {IonSnackbar} from '../../../src/notifications/IonSnackbar';
import IonTooltip from '../../components/tooltip/IonTooltip';
import useAnimatedWrapper from '../../hooks/useAnimatedWrapper';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {getFormattedDate} from '../../utils/ComponentUtils';

const VIDEO_AVAILABLE_PLACEHOLDER           = 'assets/img/ion-vision-placeholder.png';
const VIDEO_NOT_AVAILABLE_PLACEHOLDER       = 'assets/img/ion-vision-not-available.png';
const VIDEO_NOT_AVAILABLE_HOVER_PLACEHOLDER = 'assets/img/ion-vision-not-available-hover.png';

export type VisionEventItemProps = {
  event: GenericObject;
  isPhone?: boolean;
  eventClick?: (data: GenericObject) => void;
  showEventBorder?: boolean;
  hasVideo?: boolean;
  toolTipArrow?: boolean;
  toolTipPosition?: string;
  toolTipLabel?: string;
};

export type VideoElement = HTMLVideoElement & {
  requestFullscreen(): Promise<void>;
  webkitEnterFullScreen(): Promise<void>;
};

const useStyles = makeStyles(theme => {
  const border = `1px solid ${dlsColor('grey5')}`;
  return {
    eventWrapper : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'space-between'
    },
    subContainer : {
      display       : 'flex',
      flexDirection : 'column',
      height        : '48px',
      maxWidth      : 185,
      minWidth      : 0,
      marginLeft    : 10
    },
    image : {
      width      : '64px',
      height     : '47px',
      cursor     : 'pointer',
      flexShrink : 0,
      flexGrow   : 0
    },
    eventLabel : {
      ...dlsAlt('typography', 'cta2'),
      color    : ({hasVideo}: {hasVideo: boolean}) => hasVideo ? dlsColor('aaBlue') : dlsColor('grey3'),
      fontSize : 12,
      cursor   : 'pointer'
    },
    eventTimeLabel : {
      ...dlsAlt('typography', 'cta2'),
      color      : dlsColor('grey3'),
      fontWeight : 500,
      fontSize   : 10,
      marginTop  : 4
    },
    eventItem : {
      width        : '100%',
      display      : 'flex',
      padding      : '8px 15px',
      cursor       : 'pointer',
      borderBottom : ({showEventBorder}: {showEventBorder: boolean}) => showEventBorder ? border : 'none',
      '&:hover'    : {
        backgroundColor : dlsColor('paleBlue')
      }
    },
    hiddenVideoElement : {
      width   : '0px',
      height  : '0px',
      opacity : 0
    },
    videoNotAvailable : {
      height          : '100%',
      width           : '100%',
      backgroundSize  : 'cover',
      backgroundImage : `url(${VIDEO_NOT_AVAILABLE_PLACEHOLDER})`,
      '&:hover'       : {
        backgroundImage : `url(${VIDEO_NOT_AVAILABLE_HOVER_PLACEHOLDER})`
      }
    }
  };
});

/**
 * VisionEventItem component renders an individual event item for the vision accordion.
 *
 * ```typescript
 *
 * import VisionEventItem from '../../components/vision/VisionEventItem';
 *
 * <VisionEventItem event={featuredEvent}
 *  eventClick={openVisionModal(featuredEvent)}
 *  videoSource={videoSource}
 *  isPhone={isPhone} />
 *
 * ```
 *
 * @props event @Object - event object
 * @props eventClick @Function - event click handler
 * @props showEventBorder @boolean - show event border
 * @props isPhone @boolean - if the device is a phone
 */
const VisionEventItem = ({
  event,
  eventClick,
  toolTipLabel,
  isPhone = false,
  hasVideo = true,
  toolTipArrow = true,
  showEventBorder = false,
  toolTipPosition = 'right'
}: VisionEventItemProps) => {
  const classes               = useStyles({showEventBorder, hasVideo});
  const AnimatedWrapper       = useAnimatedWrapper({});
  const videoRef              = useRef<HTMLVideoElement>(null);
  const [isError, setIsError] = useState(false);

  const onRequestFullscreen = useCallback(async function() {
    const videoElement = videoRef.current as VideoElement;
    if (videoElement) {
      // chrome / firefox or mobile safari api ...
      const api = videoElement.requestFullscreen ? 'requestFullscreen' : 'webkitEnterFullScreen';
      try {
        await videoElement[api]?.();
        await videoElement?.play?.();
      } catch (err) {
        setIsError(true);
        error('Error requesting fullscreen:', err);
      }
    } else {
      setIsError(true);
    }
  }, []);

  const handleEventClick = useCallback(function(visionEvent: GenericObject) {
    if (isPhone) {
      // if the device is a phone, we want to open the video fullscreen instead of opening the modal
      hasVideo && onRequestFullscreen();
    } else {
      eventClick(visionEvent);
    }
  }, [isPhone]);

  const onCloseMessage = useCallback(function() {
    setIsError(false);
  }, []);

  useEffect(() => {
    if (isPhone && videoRef.current) {
      videoRef.current.src = event?.videoLink;
    }
  }, [isPhone, event?.videoLink]);

  // Defines a video placeholder based on values.
  const VideoPlaceholder = useMemo(() => {
    if (!hasVideo && toolTipLabel) {
      if (!isPhone) {
        return <IonTooltip title={<span style={{marginLeft: '8px'}}>{toolTipLabel}</span>}
          placement={toolTipPosition as any}
          arrow={toolTipArrow}>
          {<div className={classes.videoNotAvailable} />}
        </IonTooltip>;
      } else {
        return <div className={classes.videoNotAvailable} />;
      }
    }
    return <img style={{height: '100%', width: '100%'}} src={VIDEO_AVAILABLE_PLACEHOLDER} />;
  }, [hasVideo, toolTipLabel, isPhone, toolTipPosition, toolTipArrow]);

  return (
    <AnimatedWrapper>
      <div className={classes.eventWrapper} onClick={function() { handleEventClick(event, hasVideo); }}>
        <div className={classes.eventItem}>
          <div className={classes.image}>
            {VideoPlaceholder}
          </div>
          <div className={classes.subContainer}>
            <span className={classes.eventLabel}>
              {getEventName({
                from : EventCodeValueType?.eventCode,
                val  : parseInt(event?.avlEventCode)
              })}
            </span>
            <span className={classes.eventTimeLabel}>
              {getFormattedDate(event?.appmsgEventTime)}
            </span>
          </div>
        </div>
        {isPhone && (
          <video ref={videoRef}
            className={classes.hiddenVideoElement}
            src={event?.videoLink} />
        )}
      </div>
      {isPhone && isError && (
        <IonSnackbar message={translate('common:vision.error')}
          onAction={onCloseMessage}
          onClose={onCloseMessage}
          autoHideDuration={5000}
          show />
      )}
    </AnimatedWrapper>
  );
};

export default memo(VisionEventItem);
